import * as React from 'react';
import { clearUploadQueue, declineUploadFile, doFileUpload, getFileChangesPreview, processUploadQueue } from '../../store/upload';
import { Dialog } from 'primereact/dialog';
import { useAppDispatch, useAppSelector } from '../../store';
import { Badge } from 'primereact/badge';
import { parseFileReadableFilesize } from '../../utils/parse';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { APIStatus } from '../../utils/types';
import { Message } from 'primereact/message';
import { format, parse } from 'date-fns';
import { UploadControllerGetChangesToValidateResponse } from '../../services/api';
import { ProgressSpinner } from 'primereact/progressspinner';
import { periodFormat } from '../../utils/date';
import { classNames } from 'primereact/utils';

export default function UploadConfirmModal({ onComplete }: { onComplete: () => void }) {
  const { queuedUploads, uploadHistory } = useAppSelector((state) => state.uploads);
  const dispatch = useAppDispatch();

  const [isLoadingPreview, setIsLoadingPreview] = React.useState<boolean>(false);
  const [changePreview, setChangePreview] = React.useState<UploadControllerGetChangesToValidateResponse>();
  const [apiState, setApiState] = React.useState<APIStatus | null>(null);

  React.useEffect(() => {
    checkUploadDiff();
  }, []);

  function handleClose() {
    for (const i in queuedUploads?.files) {
      declineUploadFile(queuedUploads?.files[i].uploadId);
    }
    dispatch(clearUploadQueue());
  }

  async function checkUploadDiff() {
    setIsLoadingPreview(true);

    const res = await getFileChangesPreview({
      timePeriodDate: queuedUploads?.period
    });
    setChangePreview(res);
    setIsLoadingPreview(false);
  }

  async function doUpload() {
    try {
      setApiState(APIStatus.LOADING);
      
      await doFileUpload({
        timePeriodDate: queuedUploads?.period,
        // files: queuedUploads?.files,
        // uploadId: ,
      });

      setApiState(null);
      dispatch(processUploadQueue());
      onComplete();
    } catch (error) {
      console.log(error);
      setApiState(APIStatus.ERROR);
    }
  }

  return (
    <Dialog
      visible
      header="Confirm file upload(s)"
      footer={
        <div className="flex justify-end gap-2">
          <Button rounded severity="secondary" onClick={handleClose}>
            <span className="mr-2">cancel</span>
            <Tag>ESC</Tag>
          </Button>
          <Button loading={apiState === APIStatus.LOADING} label="yes, upload files" rounded onClick={doUpload} />
        </div>
      }
      onHide={handleClose}
    >
      {isLoadingPreview === true && (
        <div className="flex flex-col items-center justify-center gap-2 p-8">
          <ProgressSpinner className="w-8 h-8" />
          <div className="text-sm">Fetching preview of changes..</div>
        </div>
      )}

      {!isLoadingPreview && (
        <>
          {uploadHistory.length > 0 && (
            <>
              <div className="flex flex-col mb-4 -mx-6 -mt-6">
                <Message
                  className="rounded-none"
                  severity="error"
                  text={
                    <>
                      <div className="font-bold">This action cannot be undone</div>
                      <p>The files you upload will permanently replace all previous uploads for this month.</p>
                    </>
                  }
                />
              </div>
            </>
          )}

          <h2 className="font-bold">Upload summary</h2>

          <div className="flex flex-col gap-4 p-4 mt-4 rounded bg-grey-2">
            {queuedUploads?.files.map((upload, index) => {
              return (
                <div className="flex gap-2" key={index}>

                  {}

                  <div
                    className={
                      classNames(
                        "flex items-center justify-center w-8 h-8 rounded",
                        {
                          "bg-accent-green": upload.uploadId === changePreview?.emailRetentionUploadId,
                          "bg-warning-yellow": upload.uploadId === changePreview?.bafoUploadId,
                        }
                      )}
                  >📊</div>
                  <div className="flex flex-wrap items-center gap-0.5 text-sm">
                    <span>{upload.file.name}</span>
                    <span className="text-grey-7">{parseFileReadableFilesize(upload.file.size)}</span>
                  </div>
                </div>
              )
            })}

            {uploadHistory.some((upload) => upload.status === 'Complete') && changePreview && (changePreview.policiesToAdd || []).length > 0 && (
              <div className="flex flex-col gap-4">
                <Message icon="pi pi-check-circle" text={`We have found ${changePreview.policiesToAdd!.length} new group${changePreview.policiesToAdd!.length > 1 ? 's' : ''}`} />
                
                <div className="bg-white p-4 pt-0 overflow-auto max-h-[250px]">
                  <div className="bg-white sticky top-0 py-4 grid grid-cols-[2fr_1fr_65px] gap-2 font-bold">
                    <div>Group name</div>
                    <div>Policy number</div>
                    <div>Status</div>
                  </div>

                  {(changePreview?.policiesToAdd || []).map((group, index) => {
                    return (
                      <div key={index} className="pb-4 grid grid-cols-[2fr_1fr_65px] gap-2">
                        <div>{group.groupName}</div>
                        <div>{group.policyNumber}</div>
                        <div><Badge className="w-full" severity={'success'} value={'New'} /></div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              Uploading to: {format(queuedUploads?.period!, 'MMMM yyyy')} reissue date
            </div>
          </div>

          {apiState === APIStatus.ERROR && (
            <div className="flex flex-col mt-4">
              <Message severity="error" text="There was an error uploading your file(s), please try again" />
            </div>
          )}
        </>
      )}
    </Dialog>
  )
};